import { SectionHeading } from "@themes/pollinator/ui/SectionHeading";
import { CoworkersInvitationFormContainer } from "../CoworkersInvitationForm";
import { CoworkersTableContainer } from "../CoworkersTable";
import { CoworkersCreateInvitation } from "../CoworkersCreateInvitation";
import { InvitationLinkSection } from "@platform/features/invitation";
import type { Invitation } from "@core/features/invitation";

export type Props = {
  /** Organization ID */
  organizationId: number;
  /** Invitation for displaying sent invitations (coworker invitation) */
  invitation: Invitation | null;
  /** Program ID for creating new invitations */
  programId: number | null;
  /** Template ID for creating new invitations */
  templateId: number | null;
  /** Optional callback on invitation success */
  onInviteSuccess?: () => void;
};

/**
 * Coworkers tab content for organization admin page.
 * Displays team member invitation form and current team members table.
 * If no orgAdmin invitation exists, shows a button to create one.
 */
export function OrgCoworkersTab({
  organizationId,
  invitation,
  programId,
  templateId,
  onInviteSuccess,
}: Props) {
  return (
    <div
      className="w-full flex flex-col gap-12 pb-8 max-w-3xl"
      data-testid="org-coworkers-tab"
    >
      <SectionHeading
        title="Invite Team Members"
        description="Add colleagues to help manage your organization. They'll receive an email invitation to join."
      >
        {invitation ? (
          <CoworkersInvitationFormContainer
            organizationId={organizationId}
            invitationId={invitation.id}
            onSuccess={onInviteSuccess}
            showHeading={false}
          />
        ) : (
          <CoworkersCreateInvitation
            organizationId={organizationId}
            programId={programId}
            templateId={templateId}
          />
        )}
      </SectionHeading>
      <SectionHeading
        title="Coworkers"
        description="Team members who can help manage your organization."
      >
        <CoworkersTableContainer organizationId={organizationId} />
      </SectionHeading>
      {invitation && (
        <InvitationLinkSection
          shortcode={invitation.shortcode}
          title="Share Your Team Invitation Link"
          description="Share this link with colleagues to invite them to help manage your organization."
        />
      )}
    </div>
  );
}

export default OrgCoworkersTab;
